import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TableHeader } from '@huge-monorepo/hip/domain';
import { TableComponent } from '@huge-monorepo/shared/components';
import { Endpoints, GenericService, ToolsService } from '@huge-monorepo/hip/services';
import { ToolsModule } from '@huge-monorepo/shared/tools';
import { MaterialModule } from '@huge-monorepo/shared/modules';
import { CommonModule } from '@angular/common';
import { MY_DATE_PICKER_FORMATS } from '@huge-monorepo/shared/components';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { Device, TableAction, TeltonikaDevice } from '@huge-monorepo/shared/interfaces';
import { ToastrService } from 'ngx-toastr';
import { strings } from '@angular-devkit/core';

@Component({
  selector: 'app-teltonika-update-devices-log',
  templateUrl: './teltonika-update-devices-log.component.html',
  styleUrls: ['./teltonika-update-devices-log.component.scss'],
  standalone: true,
  imports: [ToolsModule, MaterialModule, CommonModule],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_PICKER_FORMATS },
  ],
})
export class TeltonikaUpdateDevicesLogComponent implements OnInit {
  @ViewChild('table', { static: false }) public table: TableComponent;

  report: any = {};

  DeviceIPControl = new UntypedFormControl('');
  UserControl = new UntypedFormControl('');

  updateDeviceLogHeaders: TableHeader[] = [
    {
      ID: 'ActionName',
      Index: 0,
      Sort: 'ActionName',
      Sortable: true,
      Text: 'Action',
      Type: String,
      Width: 150,
    },
    {
      ID: 'Description',
      Index: 1,
      Sort: 'Description',
      Sortable: true,
      Text: 'Description',
      Type: String,
      Width: 250,
      Wrapper: (obj: any, elem: any) => {
        if (!elem) return '-';
        return elem.length > 74 ? elem.substring(0, 74) + '...' : elem;
      },
      MatToolTip: (obj: any) => obj.Description,
    },
    {
      ID: 'User',
      Index: 2,
      Sort: 'User',
      Sortable: true,
      Text: 'User',
      Type: String,
      Width: 100,
      Wrapper: (_obj, elem) => {
        let ret = elem.Name;
        if (elem.Surname != '') {
          ret += ' ' + elem.Surname;
        }

        if (elem.ID == 6) {
          ret = 'Proman User';
        }
        return ret;
      },
    },
    {
      ID: 'Timestamp',
      Index: 3,
      Sort: 'Timestamp',
      Sortable: true,
      Text: 'Timestamp',
      Type: String,
      Width: 150,
      Wrapper: (_obj, elem) => {
        return this.tools.FormatDateTimeFromDateSwedish(this.tools.ParseAPIDateWithTimezone(elem));
      },
    },
  ];

  baseURL = '/teltonika/auditlogs/';
  tableObj = {
    Start: this.tools.FormatStartDate(this.tools.GetStartOfMonth(new Date()).toISOString()),
    End: this.tools.FormatEndDate(new Date().toISOString()),
    DeviceIds: null,
    UserIds: null,
    ActionName: 'Complete Update Devices By IPs',
    DeviceNumber: '',
    User: '',
    DeviceIP: '',
  };

  actions: TableAction[] = [
    {
      Icon: 'download',
      Text: 'Export CSV',
      Caller: (elem) => {
        this.exportCSV(elem.ID);
      },
    },
  ];

  /**
   * Validator that checks if the provided date is not in the future.
   * Returns a validation error if the date is missing or is a future date.
   * @returns ValidatorFn
   */
  notFutureDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return { required: true };
      }
      const dateValue = new Date(value);
      if (dateValue > new Date()) {
        return { futureDate: true };
      }
      return null;
    };
  }

  startDateControl = new FormControl<Date | null>(this.tools.GetStartOfMonth(new Date()), [this.notFutureDateValidator()]);
  endDateControl = new FormControl<Date | null>(new Date(), [this.notFutureDateValidator()]);

  constructor(private genericService: GenericService, private tools: ToolsService, private endpoints: Endpoints, private toastr: ToastrService) { }

  ngOnInit() {
    this.startDateControl.setValue(this.tools.GetStartOfMonth(new Date()));
    this.endDateControl.setValue(new Date());
    this.startDateControl.valueChanges.subscribe((value) => {
      this.updateStart(value);
    });
    this.endDateControl.valueChanges.subscribe((value) => {
      this.updateEnd(value);
    });
  }

  /**
   * Updates the report's StartDate property based on the provided date.
   * Sets the time to 00:00:00.000 for the start of the day.
   * @param date Date object or null
   */
  updateStart(date: Date | null) {
    if (!date) {
      this.report.StartDate = null;
      return;
    }
    date.setHours(0, 0, 0, 0);

    this.report.StartDate = this.tools.FormatStartDate(date.toISOString());
  }

  /**
   * Updates the report's EndDate property based on the provided date.
   * @param date Date object or null
   */
  updateEnd(date: Date | null) {
    if (!date) {
      this.report.EndDate = null;
      return;
    }
    date.setHours(23, 59, 59, 999);
    this.report.EndDate = this.tools.FormatEndDate(date.toISOString());
  }

  /**
   * Resets all filter fields to their default values and triggers a new search.
   * Clears Device IP and User controls, and sets the date controls to the current month.
   */
  resetFields() {
    this.DeviceIPControl.setValue('');
    this.startDateControl.setValue(this.tools.GetStartOfMonth(new Date()));
    this.endDateControl.setValue(new Date());
    this.UserControl.setValue('');

    this.search();
  }

  /**
   * Executes a search for Teltonika device update logs based on the current filter controls.
   */
  search() {
    if (!this.startDateControl.valid || !this.endDateControl.valid || !this.startDateControl.value || !this.endDateControl.value) {
      return;
    }

    this.tableObj.Start = this.tools.FormatStartDate(this.startDateControl.value.toISOString());
    this.tableObj.End = this.tools.FormatEndDate(this.endDateControl.value.toISOString());
    this.tableObj.ActionName = 'Complete Update Devices By IPs';
    this.tableObj.DeviceIP = this.DeviceIPControl.value;
    this.tableObj.User = this.UserControl.value;

    this.table.refresh();
  }

  /**
   * Exports the Teltonika device update logs as a CSV file.
   * @param logID The ID of the log to export.
   * @param type Boolean indicating export type (success or failure).
   */
  exportCSV(logID: number) {
    this.genericService.download(this.endpoints.GET_TELTONIKA_UPDATE_DEVICE_LOGS_EXPORT(logID), null, 'get')
      .subscribe({
        next: (result: any) => {
          if (!result && !result.body) {
            this.toastr.error("No data available for export.");
            return;
          }
          this.toastr.success("Successfully exported CSV");
        },
        error: (err) => {
          this.toastr.error(err.message);
        }
      });
  }
}
